import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ActivityScreen from "../../../components/screens/Activity/ActivityScreen";
import {
  courses as courseQuerys,
  completions as completionQuerys,
  progress as progressQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { DateTime } from "luxon";
import { getMemberCourseCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getCourseProgress } from "@nualang/nualang-api-and-queries/APIs/Progress";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  completions as completionHelpers,
  progress as progressHelpers,
  courses as courseHelpers,
} from "../../../utils/helpers";
import config from "../../../config";
import { useSubscription } from "../../../hooks";

function Course(props) {
  const [title, setTitle] = useState("");
  const [picture, setPicture] = useState("");
  const [description, setDescription] = useState("");
  const [filterName, setFilterName] = useState("");
  const [fromDate, setFromDate] = React.useState(
    DateTime.now().minus({ years: 1 }),
  );
  const [toDate, setToDate] = React.useState(DateTime.now());

  const {
    authData: { username, attributes },
    handleViewExerciseAttempt,
  } = props;
  const params = useParams();
  const { courseId, sectionId, topicId } = params;
  const { subscription } = useSubscription();
  const { t } = useTranslation();

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const isCreator =
    course.createdBy === username ||
    (course.collaborators &&
      Array.isArray(course.collaborators) &&
      course.collaborators.includes(attributes.email));

  const courseCompletionsQuery = completionQuerys.useCourseCompletions(
    async (courseId, { fromDate, toDate }) => {
      const courseCompletions = await getMemberCourseCompletions(
        username,
        courseId,
      );
      let formattedCompletions = await completionHelpers.formatCompletions(
        courseCompletions.Items,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedCompletions = formattedCompletions.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedCompletions = formattedCompletions.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      if (courseId || sectionId || topicId) {
        formattedCompletions = formattedCompletions.filter((c) => {
          if (courseId && sectionId && topicId) {
            return (
              course.courseId === courseId &&
              course.sectionId === sectionId &&
              course.topicId === topicId
            );
          }
          if (courseId && sectionId) {
            return (
              course.courseId === courseId && course.sectionId === sectionId
            );
          }
          if (courseId) {
            return course.courseId === courseId;
          }
          return true;
        });
      }
      return formattedCompletions;
    },
    {
      courseId: courseId,
      filters: {
        returnvalues:
          "returnvalues=createdByName,courseSectionTopicId,exerciseMemberId," +
          "courseSectionId,botName,exerciseStartTime,createdAt,sectionId," +
          "exercise,picture,createdBy,botId,topicId,updatedAt,courseId," +
          "memberId,username,roleplayId,roleplayName",
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!courseId && courseQuery.isSuccess },
  );

  const courseProgressQuery = progressQuerys.useCourseProgress(
    async (courseId, { fromDate, toDate }) => {
      const courseProgress = await getCourseProgress(courseId);
      let formattedProgress = await progressHelpers.formatProgress(
        courseProgress.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      if (courseId || sectionId || topicId) {
        formattedProgress = formattedProgress.filter((c) => {
          if (courseId && sectionId && topicId) {
            return (
              course.courseId === courseId &&
              course.sectionId === sectionId &&
              course.topicId === topicId
            );
          }
          if (courseId && sectionId) {
            return (
              course.courseId === courseId && course.sectionId === sectionId
            );
          }
          if (courseId) {
            return course.courseId === courseId;
          }
          return true;
        });
      }
      return formattedProgress;
    },
    {
      courseId: courseId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!courseId && courseQuery.isSuccess },
  );

  const fromDateChange = (date) => {
    setFromDate(date);
  };

  const toDateChange = (date) => {
    setToDate(date);
  };

  const filterChange = (filter) => {
    setFilterName(filter);
  };

  useEffect(() => {
    if (course?.picture && course?.courseName && course?.description) {
      setPicture(course.picture);
      setDescription(course.description);
      setTitle(course.courseName);
    }
  }, [course?.picture, course?.description, course?.courseName]);

  return (
    <ActivityScreen
      t={t}
      fromDateChange={fromDateChange}
      isCreator={isCreator}
      fromDate={fromDate}
      toDate={toDate}
      toDateChange={toDateChange}
      filterChange={filterChange}
      completions={
        courseCompletionsQuery.isSuccess &&
        Array.isArray(courseCompletionsQuery.data) &&
        courseCompletionsQuery.data.length
          ? courseCompletionsQuery.data
          : []
      }
      completionsTotal={
        courseCompletionsQuery.isSuccess &&
        Array.isArray(courseCompletionsQuery.data) &&
        courseCompletionsQuery.data.length
          ? courseCompletionsQuery.data.length
          : 0
      }
      progress={
        courseProgressQuery.isSuccess &&
        Array.isArray(courseProgressQuery.data) &&
        courseProgressQuery.data.length
          ? courseProgressQuery.data
          : []
      }
      progressTotal={
        courseProgressQuery.isSuccess &&
        Array.isArray(courseProgressQuery.data) &&
        courseProgressQuery.data.length
          ? courseProgressQuery.data.length
          : 0
      }
      handleViewEvent={handleViewExerciseAttempt}
      title={title}
      description={description}
      picture={picture}
      subscription={subscription}
      isCompletionsLoading={courseCompletionsQuery.isLoading}
      isProgressLoading={courseProgressQuery.isLoading}
      mainSiteUrl={config.SITE_URL}
    />
  );
}

Course.propTypes = {
  authData: PropTypes.object,
  themeContext: PropTypes.object,
  handleViewExerciseAttempt: PropTypes.func,
};

export default Course;
